import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader } from "lucide-react";
import {
  CategorySection,
  ActionsListSection,
  GroupsSection,
  anonymizeActions,
  submitAutomation,
} from "./OneClickAutomationDialog.utils";
import { ICompasAIChatAction } from "@/types/compass.types";
import { useRequestCategories } from "@/hooks/useRequestCategories";
import { useGroups } from "@/hooks/useGroups";
import { useAnonymizePrompt } from "@/hooks/useAnonymizePrompt";
import { useChatMessagesChain } from "@/hooks/useChatMessagesChain";
import { useQueryClient } from "@tanstack/react-query";
import {
  oneClickAutomationSchema,
  type OneClickAutomationFormValues as FormValues,
} from "@/schemas/automation.schemas";

interface OneClickAutomationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  actions: ICompasAIChatAction[];
  documentId: string;
}

const CreateRequestCategoryDialog = React.lazy(
  () => import("./request-gallery/CreateRequestCategoryDialog")
);

function buildToggleGroup(
  selectedGroups: string[],
  setValue: (name: "groups", value: string[]) => void
) {
  return (groupId: string) => {
    const newGroups = selectedGroups.includes(groupId)
      ? selectedGroups.filter((id) => id !== groupId)
      : [...selectedGroups, groupId];
    setValue("groups", newGroups);
  };
}

function buildOnSubmit(
  actions: ICompasAIChatAction[],
  documentId: string,
  chatMessagesChainMutation: ReturnType<typeof useChatMessagesChain>,
  queryClient: ReturnType<typeof useQueryClient>,
  onClose: () => void
) {
  return (data: FormValues) => {
    submitAutomation(actions, data, documentId, chatMessagesChainMutation, queryClient, onClose);
  };
}

const AutomationDialogFooter: React.FC<{
  onClose: () => void;
  isAnonymizing: boolean;
  isPending: boolean;
  actionsCount: number;
}> = ({ onClose, isAnonymizing, isPending, actionsCount }) => (
  <DialogFooter className="px-4 py-3 border-t flex flex-row items-center justify-end gap-2">
    <Button type="button" variant="outline" size="sm" onClick={onClose}>
      Cancel
    </Button>
    <Button type="submit" size="sm" disabled={isAnonymizing || isPending || actionsCount === 0}>
      {isPending ? "Creating..." : "Create"}
    </Button>
  </DialogFooter>
);

interface AutomationDialogLayoutProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isAnonymizing: boolean;
  isPending: boolean;
  actionsCount: number;
  control: ReturnType<typeof useForm<FormValues>>["control"];
  isCategoriesLoading: boolean;
  categories: { _id: string; name: string; parent_id?: string | null }[] | undefined;
  onCreateCategory: () => void;
  anonymizedActions: ICompasAIChatAction[];
  searchGroup: string;
  onSearchChange: (value: string) => void;
  isGroupsLoading: boolean;
  groupsData: { data: { id: string; name: string }[] } | undefined;
  selectedGroups: string[];
  onToggleGroup: (groupId: string) => void;
  isCreateCategoryOpen: boolean;
  setIsCreateCategoryOpen: (v: boolean) => void;
}

const AutomationDialogLayout: React.FC<AutomationDialogLayoutProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isAnonymizing,
  isPending,
  actionsCount,
  control,
  isCategoriesLoading,
  categories,
  onCreateCategory,
  anonymizedActions,
  searchGroup,
  onSearchChange,
  isGroupsLoading,
  groupsData,
  selectedGroups,
  onToggleGroup,
  isCreateCategoryOpen,
  setIsCreateCategoryOpen,
}) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-[600px] h-[80vh] p-0 flex flex-col gap-0">
      <DialogHeader className="px-4 py-3 border-b">
        <DialogTitle className="text-base font-medium">Create 1-Click Automation</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-hidden p-3 flex flex-col gap-4">
          <CategorySection
            control={control}
            isCategoriesLoading={isCategoriesLoading}
            categories={categories}
            onCreateCategory={onCreateCategory}
          />
          <ActionsListSection isAnonymizing={isAnonymizing} anonymizedActions={anonymizedActions} />
          <GroupsSection
            searchGroup={searchGroup}
            onSearchChange={onSearchChange}
            isGroupsLoading={isGroupsLoading}
            groupsData={groupsData}
            selectedGroups={selectedGroups}
            onToggleGroup={onToggleGroup}
          />
        </div>
        <AutomationDialogFooter
          onClose={onClose}
          isAnonymizing={isAnonymizing}
          isPending={isPending}
          actionsCount={actionsCount}
        />
      </form>
      <React.Suspense fallback={<Loader className="animate-spin" />}>
        <CreateRequestCategoryDialog
          open={isCreateCategoryOpen}
          onOpenChange={setIsCreateCategoryOpen}
        />
      </React.Suspense>
    </DialogContent>
  </Dialog>
);

const OneClickAutomationDialog: React.FC<OneClickAutomationDialogProps> = ({
  isOpen,
  onClose,
  actions,
  documentId,
}) => {
  const [anonymizedActions, setAnonymizedActions] = React.useState<ICompasAIChatAction[]>([]);
  const [isAnonymizing, setIsAnonymizing] = React.useState(false);
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = React.useState(false);
  const [searchGroup, setSearchGroup] = React.useState("");

  const { data: categories, isLoading: isCategoriesLoading } = useRequestCategories();
  const { data: groupsData, isLoading: isGroupsLoading } = useGroups();
  const anonymizePromptMutation = useAnonymizePrompt();
  const chatMessagesChainMutation = useChatMessagesChain();
  const queryClient = useQueryClient();

  const { control, handleSubmit, setValue, watch, reset } = useForm<FormValues>({
    resolver: zodResolver(oneClickAutomationSchema),
    defaultValues: { categoryId: "", groups: [] },
  });

  const selectedGroups = watch("groups");

  React.useEffect(() => {
    if (isOpen && actions.length > 0) {
      anonymizeActions(
        actions,
        anonymizePromptMutation.mutateAsync,
        setAnonymizedActions,
        setIsAnonymizing
      );
    } else {
      setAnonymizedActions([]);
      reset({ categoryId: "", groups: [] });
    }
    // anonymizePromptMutation.mutateAsync is intentionally omitted: TanStack Query
    // guarantees stable mutateAsync references across renders (v5 behaviour).
  }, [isOpen, actions, reset]);

  const toggleGroup = buildToggleGroup(selectedGroups, setValue);
  const onSubmit = buildOnSubmit(
    actions,
    documentId,
    chatMessagesChainMutation,
    queryClient,
    onClose
  );

  return (
    <AutomationDialogLayout
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      isAnonymizing={isAnonymizing}
      isPending={chatMessagesChainMutation.isPending}
      actionsCount={actions.length}
      control={control}
      isCategoriesLoading={isCategoriesLoading}
      categories={categories}
      onCreateCategory={() => setIsCreateCategoryOpen(true)}
      anonymizedActions={anonymizedActions}
      searchGroup={searchGroup}
      onSearchChange={setSearchGroup}
      isGroupsLoading={isGroupsLoading}
      groupsData={groupsData}
      selectedGroups={selectedGroups}
      onToggleGroup={toggleGroup}
      isCreateCategoryOpen={isCreateCategoryOpen}
      setIsCreateCategoryOpen={setIsCreateCategoryOpen}
    />
  );
};

export default OneClickAutomationDialog;
